import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';
import './CandidateRegistration.css';
import showPopup from '../utils/showPopup';

function CandidateRegistration() {
  // SVG Eye Icon
  const EyeIcon = ({ visible }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#888"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" fill={visible ? '#888' : 'none'} />
    </svg>
  );
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    // TG ID Card
    hasTGId: '',
    tgIdNumber: '',
    tgIdCard: null,
    
    // Personal Details
    dateOfBirth: '',
    candidateName: '',
    gender: '',
    
    // Contact Details
    mobileNumber: '',
    alternateMobileNumber: '',
    emailId: '',
    
    // Address Proof
    addressIdType: '',
    addressIdProof: null,
    
    // Address Details
    district: '',
    mandal: '',
    city: '',
    pincode: '',
    
    // Job Preferences
  preferredJobLocation: '',
  educationalQualification: '',
  certificateFile: null,
  resumeFile: null,
  preferredJobSector: '',
    
    // Skills
    skills: [],
    
    // Password
    password: '',
    confirmPassword: ''
  });

  // Skills temporary selection for dropdown and custom input
  const [selectedSkill, setSelectedSkill] = useState('');
  const [showCustomSkill, setShowCustomSkill] = useState(false);
  const [customSkill, setCustomSkill] = useState('');

  // Error state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Terms and Conditions modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // DOB calendar picker ref
  const dobPickerRef = useRef(null);

  // District options (33 districts)
  const districts = [
    'Adilabad', 'Bhadradri Kothagudem', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally',
    'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Kumuram Bheem', 'Mahabubabad',
    'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu',
    'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad',
    'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet',
    'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal Rural', 'Warangal Urban',
    'Yadadri Bhuvanagiri', 'Hyderabad', 'Secunderabad', 'Khammam'
  ];

  // Educational qualifications
  const qualifications = [
    '10th fail or below',
    '10th Pass',
    '12th Pass',
    'Graduate (B.A, B.Com, B.Sc, BCA, BE/B.Tech, B.Pharm, BBA, MBBS, Other)',
    'Post Graduate (M.A, M.Com, M.Sc, MD, M.Tech, M.Pharm, MBA, MCA, MSW, Other)',
    'Doctorate/PhD',
    'Diploma',
    'ITI'
  ];

  // Job sectors
  const jobSectors = [
    'IT/Computer - Hardware & Networking',
    'IT/Computer - Software',
    'ITES/BPO',
    'KPO/Research/Analytics',
    'Telecom',
    'Internet Service Provider (ISP)',
    'Hotels/Hospitality/Restaurant',
    'Airlines/Aviation/Aerospace',
    'Travel/Tourism',
    'Consulting/Advisory Services',
    'Recruitment/Staffing/RPO',
    'Advertising/PR/Events',
    'Facility Management',
    'Public Relations PR',
    'Legal/Law Firm',
    'Customer Service',
    'Law Enforcement/Security Services',
    'Entertainment/Media/Publishing',
    'Environmental Service',
    'Market Research',
    'Social Media',
    'Education/E-learning',
    'Engineering & Design Jobs',
    'Automotive and Auto Ancillaries',
    'Machinery/Equipment Mfg',
    'Electrical/Switchgears',
    'Chemicals/Petrochemicals',
    'Metals and Mining',
    'Escalator/Elevator',
    'Consumer Electronics/Durables/Appliances',
    'Heat Ventilation Air Conditioning (HVAC)',
    'Semiconductor/Semiconductor Manufacturing',
    'Glass Industry',
    'Iron & Steel Industry',
    'Plastic/Rubber Industry',
    'Electronics Manufacturing',
    'Paints',
    'Paper Industry',
    'Printing/Packaging',
    'Textiles/Yarn/Fabrics/Garments',
    'Tyres',
    'Banking/Accounting/Financial Services',
    'Insurance',
    'Financial Markets',
    'FMCG',
    'Retailing',
    'General Trading/Import/Export',
    'Food & Packaged Food',
    'Beverages/Liquor',
    'Real Estate',
    'Architecture/Interior Design',
    'Construction & Engineering',
    'Cement/Concrete/Readymix',
    'Engineering/Procurement/Construction',
    'Ceramics & Sanitary Ware',
    'Office Equipment/Automation',
    'Railways/Infrastructure',
    'Power/Energy',
    'Alternate Energy',
    'Oil/Gas/Petroleum',
    'Hospitals/Healthcare/Diagnostics',
    'Pharmaceuticals',
    'Medical Transcription',
    'Bio Technology & Life Sciences',
    'Shipping/Marine Services',
    'Courier/Freight/Transportation',
    'Maritime Transportation',
    'Agricultural/Dairy/Forestry/Fishing',
    'Fertilizer/Pesticide',
    'Sugar Industry',
    'Fashion/Apparels',
    'Wellness/Fitness/Sports',
    'Gems & Jewellery',
    'NGOs/Social Services',
    'Government/PSU/Defence',
    'Local Market',
    'E-commerce',
    'Other'
  ];

  // Skills
  const availableSkills = [
    'Communication',
    'Computer Basics',
    'Tailoring',
    'Makeup/Beauty Service',
    'Driving',
    'Cooking/Hotel Management',
    'Typing/Data Entry',
    'Jute Bag Making'
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, files, multiple, selectedOptions } = e.target;
    
    if (type === 'file') {
      if (name === 'certificateFile') {
        setFormData(prev => ({
          ...prev,
          certificateFile: files[0]
        }));
        setErrors(prev => ({ ...prev, certificateFile: '' }));
        return;
      }
      if (name === 'resumeFile') {
        setFormData(prev => ({
          ...prev,
          resumeFile: files[0]
        }));
        setErrors(prev => ({ ...prev, resumeFile: '' }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
      setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    } else if (type === 'checkbox') {
      const { checked } = e.target;
      if (checked) {
        setFormData(prev => ({
          ...prev,
          skills: [...prev.skills, value]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          skills: prev.skills.filter(skill => skill !== value)
        }));
      }
    } else {
      // Special handling for DOB text: enforce dd-mm-yyyy while typing
      if (name === 'dateOfBirth') {
        const formatted = formatDobInput(value);
        setFormData(prev => ({ ...prev, dateOfBirth: formatted }));
      } else if (name === 'mobileNumber') {
        const formatted = formatIndianMobileInput(value);
        setFormData(prev => ({ ...prev, mobileNumber: formatted }));
      } else if (name === 'alternateMobileNumber') {
        const formatted = formatIndianMobileInput(value);
        setFormData(prev => ({ ...prev, alternateMobileNumber: formatted }));
      } else if (name === 'emailId') {
        // Trim spaces inside email as user types
        const cleaned = value.replace(/\s+/g, '');
        setFormData(prev => ({ ...prev, emailId: cleaned }));
      } else if (name === 'pincode') {
        const onlyDigits = value.replace(/[^0-9]/g, '').slice(0, 6);
        setFormData(prev => ({ ...prev, pincode: onlyDigits }));
      } else if (name === 'mandal') {
        const cleaned = value.replace(/[^A-Za-z\s]/g, '');
        setFormData(prev => ({ ...prev, mandal: cleaned }));
      } else if (name === 'city') {
        const cleaned = value.replace(/[^A-Za-z\s]/g, '');
        setFormData(prev => ({ ...prev, city: cleaned }));
      } else if (name === 'candidateName') {
        // Allow only letters and spaces, limit to 30 characters
        const cleaned = value.replace(/[^A-Za-z\s]/g, '').slice(0, 30);
        setFormData(prev => ({ ...prev, candidateName: cleaned }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Format raw input to dd-mm-yyyy (only digits allowed, auto-insert hyphens)
  const formatDobInput = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, 8); // ddmmyyyy
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    let out = '';
    if (day) out += day;
    if (month) out += (out ? '-' : '') + month;
    if (year) out += (out ? '-' : '') + year;
    return out;
  };

  // Format Indian mobile to "+91 ##########" while typing; numbers only
  const formatIndianMobileInput = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '');
    // Normalize by removing country code if user typed it
    let rest = digits;
    if (rest.startsWith('91')) {
      rest = rest.slice(2);
    }
    // Enforce starting digit to be 6/7/8/9 by dropping any invalid leading digits
    rest = rest.replace(/^[0-5]+/, '');
    // Limit to exactly 10 digits max while typing
    rest = rest.slice(0, 10);
    return rest.length > 0 ? `+91 ${rest}` : '+91 ';
  };

  // Open native calendar
  const openDobCalendar = () => {
    if (dobPickerRef.current) {
      if (dobPickerRef.current.showPicker) {
        dobPickerRef.current.showPicker();
      } else {
        dobPickerRef.current.click();
      }
    }
  };

  // When a date is picked from native calendar, sync to dd-mm-yyyy
  const handleDobPicked = (e) => {
    const iso = e.target.value; // yyyy-mm-dd
    if (!iso) return;
    const [y, m, d] = iso.split('-');
    const formatted = `${d}-${m}-${y}`;
    setFormData(prev => ({ ...prev, dateOfBirth: formatted }));
    if (errors.dateOfBirth) {
      setErrors(prev => ({ ...prev, dateOfBirth: '' }));
    }
  };

  // Helper to compute min/max for native calendar (allow up to today; cap at 100 years back)
  const toIsoDate = (dt) => dt.toISOString().slice(0, 10);
  const yearsAgo = (n) => {
    const t = new Date();
    t.setFullYear(t.getFullYear() - n);
    return t;
  };
  const dobMaxIso = toIsoDate(new Date());
  const dobMinIso = toIsoDate(yearsAgo(100));

  // File validation
  const validateFile = (file, maxSize = 1024 * 1024) => {
  if (!file) return '';
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return 'Only .jpg, .jpeg, .png, .pdf file types are allowed';
    }
    
    if (file.size > maxSize) {
      return 'File size should be below 1 MB';
    }
    
    return '';
  };

  // Form validation
  const validateForm = async () => {
    const newErrors = {};

    // TG ID Card validation
    if (formData.hasTGId === '') {
      newErrors.hasTGId = 'Please select whether you have TG ID Card';
    }
    
    if (formData.hasTGId === 'yes') {
      if (!formData.tgIdNumber.trim()) {
  newErrors.tgIdNumber = '';
      }
      const tgIdError = validateFile(formData.tgIdCard);
      if (tgIdError) {
        newErrors.tgIdCard = tgIdError;
      }
    }

    // Personal details validation
    if (!formData.dateOfBirth) {
  newErrors.dateOfBirth = '';
    } else {
      const dobStr = formData.dateOfBirth.trim();
      const match = dobStr.match(/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/);
      const today = new Date();
      const currentYear = today.getFullYear();
      if (!match) {
        newErrors.dateOfBirth = 'Use format dd-mm-yyyy (e.g., 01-01-1999)';
      } else {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);
        if (year < 1900 || year > currentYear) {
          newErrors.dateOfBirth = 'Year must be 4 digits between 1900 and ' + currentYear;
        } else {
          const dt = new Date(year, month - 1, day);
          const isValidDate = dt.getFullYear() === year && (dt.getMonth() + 1) === month && dt.getDate() === day;
          if (!isValidDate) {
            newErrors.dateOfBirth = 'Enter a valid date';
          } else if (dt > today) {
            newErrors.dateOfBirth = 'Date of Birth cannot be in the future';
          } else {
            // Only sanity cap at 100 years; allow future up to current date
            let age = currentYear - year;
            const hasHadBirthdayThisYear = (today.getMonth() + 1 > month) || ((today.getMonth() + 1 === month) && today.getDate() >= day);
            if (!hasHadBirthdayThisYear) age -= 1;
            if (age > 100) {
              newErrors.dateOfBirth = 'Enter a realistic date (age should be ≤ 100)';
            }
          }
        }
      }
    }
    
    if (!formData.candidateName.trim()) {
      newErrors.candidateName = '';
    } else if (!/^[A-Za-z\s]{1,30}$/.test(formData.candidateName)) {
      newErrors.candidateName = 'Use only letters and spaces, max 30 characters';
    }
    
    if (!formData.gender) {
      newErrors.gender = '';
    }

    // Contact details validation
  if (!formData.mobileNumber.trim()) {
  newErrors.mobileNumber = "";
      } else {
        const m = formData.mobileNumber.trim();
        const validMobile = /^\+91\s?[6-9][0-9]{9}$/.test(m);
        if (!validMobile) {
          newErrors.mobileNumber =
            "Enter valid Indian mobile (format: +91 9876543210)";
        }
      }

    // Alternate mobile number validation (optional, but validate format if provided)
    if (formData.alternateMobileNumber.trim()) {
      const altM = formData.alternateMobileNumber.trim();
      const validMobile = /^\+91\s?[6-9][0-9]{9}$/.test(altM);
      if (!validMobile) {
        newErrors.alternateMobileNumber =
          "Enter valid Indian mobile (format: +91 9876543210)";
      }
    }
    
    if (!formData.emailId.trim()) {
      newErrors.emailId = '';
    } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.emailId)) {
      newErrors.emailId = 'Please enter a valid email address';
    }

    // Address proof validation
    if (!formData.addressIdType) {
      newErrors.addressIdType = 'Please select Address ID Type';
    }
    
    const addressProofError = validateFile(formData.addressIdProof);
    if (addressProofError) {
      newErrors.addressIdProof = addressProofError;
    }

    // Address details validation
    if (!formData.district) {
      newErrors.district = '';
    }
    
    if (!formData.mandal.trim()) {
      newErrors.mandal = '';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = '';
    }
    
    if (!formData.pincode.trim()) {
      newErrors.pincode = '';
    } else if (!/^[1-9][0-9]{5}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    // Job preferences validation
    if (!formData.preferredJobLocation) {
      newErrors.preferredJobLocation = '';
    }
    
    if (!formData.educationalQualification) {
      newErrors.educationalQualification = '';
    }
    // Certificate file validation: only if not 10th fail or below
    if (formData.educationalQualification && formData.educationalQualification !== '10th fail or below') {
      const certError = validateFile(formData.certificateFile);
      if (certError) newErrors.certificateFile = certError;
    }
    // Resume file validation: always required
    if (!formData.resumeFile) {
      newErrors.resumeFile = 'Please upload your resume';
    } else {
      const allowedResumeTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedResumeTypes.includes(formData.resumeFile.type)) {
        newErrors.resumeFile = 'Only .pdf, .doc, .docx files allowed';
      } else if (formData.resumeFile.size > 2 * 1024 * 1024) {
        newErrors.resumeFile = 'Resume file size should be below 2 MB';
      }
    }
    
    if (!formData.preferredJobSector) {
      newErrors.preferredJobSector = '';
    }

    // Skills validation
    if (formData.skills.length === 0) {
      newErrors.skills = '';
    }

      // Password validation
      if (!formData.password) {
        newErrors.password = 'You missed this';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      }
      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'You missed this';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

    if (Object.keys(newErrors).length > 0) {
      const missingFields = Object.keys(newErrors)
        .filter(key => newErrors[key] === '' || typeof newErrors[key] === 'string')
        .map(key => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
      await showPopup('Please fill all required fields:\n' + missingFields.join(', '), 'Validation', { type: 'info' });
      setErrors(newErrors);
      return false;
    }
    setErrors(newErrors);
    return true;
  }


  // Add selected skill from dropdown to list
  const handleAddSkill = () => {
    if (selectedSkill === 'other') {
      setShowCustomSkill(true);
    } else if (selectedSkill && !formData.skills.includes(selectedSkill)) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, selectedSkill] }));
      setSelectedSkill('');
    }
  };

  // Add custom skill from input
  const handleAddCustomSkill = () => {
    const skill = customSkill.trim();
    if (skill && !formData.skills.includes(skill)) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, skill] }));
      setCustomSkill('');
      setShowCustomSkill(false);
      setSelectedSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skillToRemove)
    }));
  };

  // Handle Terms acceptance and proceed with submission
  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    // Trigger form submission after accepting terms
    setTimeout(() => {
      document.querySelector('.registration-form').dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }, 100);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;

    // Show terms and conditions modal before submitting
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }

    setIsSubmitting(true);
    const form = new FormData();
    for (let key in formData) {
      if (key === "skills") {
        form.append("skills", formData.skills.join(",")); // send as CSV
      } else {
        form.append(key, formData[key]);
      }
    }
    // Also provide an ISO-formatted DOB as a helper field (yyyy-mm-dd) if possible
    if (formData.dateOfBirth) {
      const dmy = formData.dateOfBirth.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (dmy) {
        const iso = `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
        form.append('dateOfBirthIso', iso);
      } else {
        // try Date parse
        const parsed = new Date(formData.dateOfBirth);
        if (!isNaN(parsed.getTime())) form.append('dateOfBirthIso', parsed.toISOString().slice(0,10));
      }
    }

    try {
      const response = await fetch("https://tstranscareers.in/api/register", {
        method: "POST",
        body: form
      });

      const result = await response.json();
      if (response.ok) {
        // If backend returns created user, persist session and navigate to dashboard
        if (result.user) {
          // Persist session and profile
          localStorage.setItem('candidateLoggedIn', JSON.stringify({ id: result.user.id, name: result.user.candidateName }));
          localStorage.setItem('candidateData', JSON.stringify(result.user));

          // If user uploaded a resume during registration, convert to dataURL and save
          try {
            const resumeFile = formData.resumeFile;
            if (resumeFile) {
              await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  try {
                    const dataUrl = reader.result;
                    const resumeObj = {
                      name: resumeFile.name,
                      size: resumeFile.size,
                      type: resumeFile.type,
                      uploadedAt: Date.now(),
                      dataUrl
                    };
                    try {
                      localStorage.setItem(`candidateResume_${result.user.id}`, JSON.stringify(resumeObj));
                    } catch (e) {
                      // localStorage might fail if quota exceeded; ignore so registration still completes
                      console.warn('Failed to save resume to localStorage:', e);
                    }
                    resolve();
                  } catch (e) {
                    reject(e);
                  }
                };
                reader.onerror = (err) => reject(err);
                reader.readAsDataURL(resumeFile);
              });
            }
          } catch (e) {
            console.warn('Error while persisting resume at registration', e);
          }

          await showPopup(result.message || 'Registration successful. You are now logged in.', 'Success', { type: 'success' });
          navigate('/candidate-dashboard');
        } else {
          await showPopup(result.message || 'Registration successful. Please login.', 'Success', { type: 'success' });
          navigate('/login');
        }
      } else {
        await showPopup(result.message || "Registration failed", 'Error', { type: 'error' });
      }
    } catch (err) {
      console.error(err);
      await showPopup("Error connecting to server", 'Error', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="candidate-registration">
      <div className="container">
        {/* centralized Back to Home button */}
        <BackHomeButton style={{ margin: '1rem 0', padding: '0.5rem 1.5rem', background: '#4169E1', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }} />
        <div className="registration-header">
          <h1>Candidate Registration</h1>
          <p>Join our employment portal and find your dream job</p>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          {/* TG ID Card Section */}
          <div className="form-section">
            <h2>1. TG ID Card Information</h2>
            <div className="form-group">
              <label className="required">Do you have TG ID Card?</label>
              <div className="custom-radio-group">
                <label className="custom-radio-label">
                  <input
                    type="radio"
                    name="hasTGId"
                    value="yes"
                    checked={formData.hasTGId === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span className="custom-radio-circle"></span>
                  <span className="radio-text">Yes</span>
                </label>
                <label className="custom-radio-label">
                  <input
                    type="radio"
                    name="hasTGId"
                    value="no"
                    checked={formData.hasTGId === 'no'}
                    onChange={handleInputChange}
                  />
                  <span className="custom-radio-circle"></span>
                  <span className="radio-text">No</span>
                </label>
              </div>
              {formData.hasTGId === 'no' && (
                <div style={{ marginTop: '0.75rem', color: '#d32f2f', fontWeight: 600, fontSize: '1.1rem' }}>
                  HELPLINE NUMBER 155326
                </div>
              )}
              {errors.hasTGId && <span className="error">{errors.hasTGId}</span>}
            </div>

            {formData.hasTGId === 'yes' && (
              <>
                <div className="form-row">
                  <div className={`form-group ${errors.tgIdNumber ? 'has-error' : ''}`}>
                    <label htmlFor="tgIdNumber" className="required">TG ID Number</label>
                    <input
                      type="text"
                      id="tgIdNumber"
                      name="tgIdNumber"
                      value={formData.tgIdNumber}
                      onChange={handleInputChange}
                      placeholder="Enter TG ID Number"
                      className={errors.tgIdNumber ? 'input-error' : ''}
                    />
                    {errors.tgIdNumber && <span className="error">{errors.tgIdNumber}</span>}
                  </div>

                  <div className={`form-group ${errors.tgIdCard ? 'has-error' : ''}`}>
                    <label htmlFor="tgIdCard" className="required">Upload TG ID Card</label>
                    <input
                      type="file"
                      id="tgIdCard"
                      name="tgIdCard"
                      onChange={handleInputChange}
                      accept=".jpg,.jpeg,.png,.pdf"
                      className={errors.tgIdCard ? 'input-error' : ''}
                    />
                    <small>Only .jpg, .jpeg, .png, .pdf files allowed (Max size: 1 MB)</small>
                    {errors.tgIdCard && <span className="error">{errors.tgIdCard}</span>}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Personal Details Section */}
          <div className="form-section">
            <h2>2. Personal Details</h2>
            
            <div className="form-row personal-row">
              <div className={`form-group ${errors.dateOfBirth ? 'has-error' : ''}`}>
                <label htmlFor="dateOfBirth" className="required">Date of Birth</label>
                <div className="dob-row">
                  <input
                    type="text"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    placeholder="dd-mm-yyyy"
                    maxLength="10"
                    inputMode="numeric"
                    className={errors.dateOfBirth ? 'input-error' : ''}
                  />
                  <button type="button" className="calendar-btn" onClick={openDobCalendar} aria-label="Open calendar">📅</button>
                  {/* Hidden native date input to open calendar; syncs back to dd-mm-yyyy */}
                  <input
                    ref={dobPickerRef}
                    type="date"
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                    max={dobMaxIso}
                    min={dobMinIso}
                    onChange={handleDobPicked}
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                </div>
                {errors.dateOfBirth && <span className="error">{errors.dateOfBirth}</span>}
              </div>

              <div className={`form-group ${errors.candidateName ? 'has-error' : ''}`}>
                <label htmlFor="candidateName" className="required">Candidate Name</label>
                <input
                  type="text"
                  id="candidateName"
                  name="candidateName"
                  value={formData.candidateName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={errors.candidateName ? 'input-error' : ''}
                />
                {errors.candidateName && <span className="error">{errors.candidateName}</span>}
                </div>

                <div className={`form-group ${errors.gender ? 'has-error' : ''}`}>
                  <label htmlFor="gender" className="required">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={errors.gender ? 'input-error' : ''}
                  >
                    <option value="">Select Gender</option>
                    <option value="trans-men">Trans-Men</option>
                    <option value="trans-women">Trans-Women</option>
                    <option value="gender-non-conforming">Gender Non-Conforming</option>
                    <option value="queer">Queer</option>
                  </select>
                  {errors.gender && <span className="error">{errors.gender}</span>}
                </div>

              </div>
          </div>

          {/* Contact Details Section */}
          <div className="form-section">
            <h2>3. Contact Details</h2>
            
            <div className="form-row">
              <div className={`form-group ${errors.mobileNumber ? 'has-error' : ''}`}>
                <label htmlFor="mobileNumber" className="required">Mobile Number</label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="14"
                  className={errors.mobileNumber ? 'input-error' : ''}
                />
                {errors.mobileNumber && <span className="error">{errors.mobileNumber}</span>}
              </div>

              <div className={`form-group ${errors.alternateMobileNumber ? 'has-error' : ''}`}>
                <label htmlFor="alternateMobileNumber">Alternate Mobile Number</label>
                <input
                  type="tel"
                  id="alternateMobileNumber"
                  name="alternateMobileNumber"
                  value={formData.alternateMobileNumber}
                  onChange={handleInputChange}
                  placeholder="Enter alternate mobile number (optional)"
                  maxLength="14"
                  className={errors.alternateMobileNumber ? 'input-error' : ''}
                />
                {errors.alternateMobileNumber && <span className="error">{errors.alternateMobileNumber}</span>}
              </div>

              <div className={`form-group ${errors.emailId ? 'has-error' : ''}`}>
                <label htmlFor="emailId" className="required">Email ID</label>
                <input
                  type="email"
                  id="emailId"
                  name="emailId"
                  value={formData.emailId}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  className={errors.emailId ? 'input-error' : ''}
                />
                {errors.emailId && <span className="error">{errors.emailId}</span>}
              </div>
            </div>
          </div>

          {/* Address Proof Section */}
          <div className="form-section">
            <h2>4. Address Proof</h2>
            <div className="form-row">
              <div className={`form-group ${errors.addressIdType ? 'has-error' : ''}`}>
                <label htmlFor="addressIdType" className="required">Choose Address ID Type</label>
                <select
                  id="addressIdType"
                  name="addressIdType"
                  value={formData.addressIdType}
                  onChange={handleInputChange}
                  className={errors.addressIdType ? 'input-error' : ''}
                >
                  <option value="">Select ID Type</option>
                  <option value="aadhar">Aadhar Card</option>
                  <option value="pan">PAN Card</option>
                  <option value="voter">Voter ID</option>
                  <option value="passport">Passport</option>
                  <option value="driving">Driving License</option>
                </select>
                {errors.addressIdType && <span className="error">{errors.addressIdType}</span>}
              </div>

              <div className={`form-group ${errors.addressIdProof ? 'has-error' : ''}`}>
                <label htmlFor="addressIdProof" className="required">Upload ID Proof</label>
                <input
                  type="file"
                  id="addressIdProof"
                  name="addressIdProof"
                  onChange={handleInputChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                  className={errors.addressIdProof ? 'input-error' : ''}
                />
                <small>Only .jpg, .jpeg, .png, .pdf files allowed (Max size: 1 MB)</small>
                {errors.addressIdProof && <span className="error">{errors.addressIdProof}</span>}
              </div>
            </div>
          </div>

          {/* Address Details Section */}
          <div className="form-section">
            <h2>5. Address for Communication</h2>
            
            <div className="form-row">
              <div className={`form-group ${errors.district ? 'has-error' : ''}`}>
                <label htmlFor="district" className="required">District</label>
                <select
                  id="district"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className={errors.district ? 'input-error' : ''}
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
                {errors.district && <span className="error">{errors.district}</span>}
              </div>

              <div className={`form-group ${errors.mandal ? 'has-error' : ''}`}>
                <label htmlFor="mandal" className="required">Mandal/Taluka/Block/Sub-division</label>
                <input
                  type="text"
                  id="mandal"
                  name="mandal"
                  value={formData.mandal}
                  onChange={handleInputChange}
                  placeholder="Enter Mandal/Taluka/Block/Sub-division"
                  className={errors.mandal ? 'input-error' : ''}
                />
                {errors.mandal && <span className="error">{errors.mandal}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.city ? 'has-error' : ''}`}>
                <label htmlFor="city" className="required">City/Village</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter City/Village"
                  className={errors.city ? 'input-error' : ''}
                />
                {errors.city && <span className="error">{errors.city}</span>}
              </div>

              <div className={`form-group ${errors.pincode ? 'has-error' : ''}`}>
                <label htmlFor="pincode" className="required">Pincode</label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit pincode"
                  maxLength="6"
                  className={errors.pincode ? 'input-error' : ''}
                />
                {errors.pincode && <span className="error">{errors.pincode}</span>}
              </div>
            </div>
          </div>

          {/* Job Preferences Section */}
          <div className="form-section">
            <h2>6. Job Preferences</h2>
            
            <div className="form-row">
              <div className={`form-group ${errors.preferredJobLocation ? 'has-error' : ''}`}>
                <label htmlFor="preferredJobLocation" className="required">Select Preferred Job Location</label>
                <select
                  id="preferredJobLocation"
                  name="preferredJobLocation"
                  value={formData.preferredJobLocation}
                  onChange={handleInputChange}
                  className={errors.preferredJobLocation ? 'input-error' : ''}
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
                {errors.preferredJobLocation && <span className="error">{errors.preferredJobLocation}</span>}
              </div>

              <div className={`form-group ${errors.educationalQualification ? 'has-error' : ''}`}>
                <label htmlFor="educationalQualification" className="required">Educational Qualification</label>
                <select
                  id="educationalQualification"
                  name="educationalQualification"
                  value={formData.educationalQualification}
                  onChange={handleInputChange}
                  className={errors.educationalQualification ? 'input-error' : ''}
                >
                  <option value="">Select Qualification</option>
                  {qualifications.map(qual => (
                    <option key={qual} value={qual}>{qual}</option>
                  ))}
                </select>
                {errors.educationalQualification && <span className="error">{errors.educationalQualification}</span>}
              </div>
              {/* Certificate Upload: Only show if not 10th fail or below */}
              {formData.educationalQualification && formData.educationalQualification !== '10th fail or below' && (
                <div className={`form-group ${errors.certificateFile ? 'has-error' : ''}`}>
                  <label htmlFor="certificateFile" className="required">Upload Qualification Certificate</label>
                  <input
                    type="file"
                    id="certificateFile"
                    name="certificateFile"
                    onChange={handleInputChange}
                    accept=".jpg,.jpeg,.png,.pdf"
                    className={errors.certificateFile ? 'input-error' : ''}
                  />
                  <small>Only .jpg, .jpeg, .png, .pdf files allowed (Max size: 1 MB)</small>
                  {errors.certificateFile && <span className="error">{errors.certificateFile}</span>}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.preferredJobSector ? 'has-error' : ''}`}>
                <label htmlFor="preferredJobSector" className="required">Preferred Job Sector</label>
                <select
                  id="preferredJobSector"
                  name="preferredJobSector"
                  value={formData.preferredJobSector}
                  onChange={handleInputChange}
                  className={errors.preferredJobSector ? 'input-error' : ''}
                >
                  <option value="">Select Job Sector</option>
                  {jobSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                {errors.preferredJobSector && <span className="error">{errors.preferredJobSector}</span>}
              </div>

              <div className={`form-group ${errors.resumeFile ? 'has-error' : ''}`}>
                <label htmlFor="resumeFile" className="required">Upload Resume</label>
                <input
                  type="file"
                  id="resumeFile"
                  name="resumeFile"
                  onChange={handleInputChange}
                  accept=".pdf,.doc,.docx"
                  className={errors.resumeFile ? 'input-error' : ''}
                />
                <small>Only .pdf, .doc, .docx files allowed (Max size: 2 MB)</small>
                {errors.resumeFile && <span className="error">{errors.resumeFile}</span>}
              </div>
            </div>
          </div>

          {/* Skills Section */}
          <div className="form-section">
            <h2>7. Skills You Possess</h2>
            
            <div className={`form-group ${errors.skills ? 'has-error' : ''}`}>
              <label htmlFor="skillsSelect" className="required">Select Skills</label>
              <div className="skills-select-row" style={{ gap: '0.5rem', display: 'flex', flexWrap: 'nowrap', width: '100%', alignItems: 'center' }}>
                {!showCustomSkill ? (
                  <>
                    <select
                      id="skillsSelect"
                      name="skillsSelect"
                      value={selectedSkill}
                      onChange={(e) => setSelectedSkill(e.target.value)}
                      className={errors.skills ? 'input-error' : ''}
                      style={{ minWidth: 180, flex: '1 1 auto' }}
                    >
                      <option value="">Select a skill</option>
                      {availableSkills.map(skill => (
                        <option key={skill} value={skill}>{skill}</option>
                      ))}
                      <option value="other">Other</option>
                    </select>
                    <button type="button" className="add-skill-btn" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }} onClick={handleAddSkill}>Add</button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Enter your skill"
                      value={customSkill}
                      onChange={e => setCustomSkill(e.target.value)}
                      style={{ minWidth: 140, padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc', flex: '1 1 auto' }}
                    />
                    <button type="button" className="add-skill-btn" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }} onClick={handleAddCustomSkill}>Add</button>
                    <button type="button" className="add-skill-btn" style={{ background: '#ccc', color: '#222', whiteSpace: 'nowrap', flex: '0 0 auto' }} onClick={() => { setShowCustomSkill(false); setCustomSkill(''); setSelectedSkill(''); }}>Cancel</button>
                  </>
                )}
              </div>
              {formData.skills.length > 0 && (
                <div className="skills-chips">
                  {formData.skills.map(skill => (
                    <span key={skill} className="skill-chip">
                      {skill}
                      <button type="button" aria-label={`Remove ${skill}`} onClick={() => handleRemoveSkill(skill)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {errors.skills && <span className="error">{errors.skills}</span>}
            </div>
          </div>

          {/* Password Section */}
          <div className="form-section">
            <h2>8. Create Login Credentials</h2>
            
            <div className="form-row">
              <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
                <label htmlFor="password" className="required">Create Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a strong password (min 8 characters)"
                    className={errors.password ? 'input-error' : ''}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon visible={showPassword} />
                  </button>
                </div>
                <small style={{ color: '#888', fontSize: '0.95rem' }}>
                  Password must be at least 8 characters long.
                </small>
                {errors.password && <span className="error">{errors.password}</span>}
              </div>

              <div className={`form-group ${errors.confirmPassword ? 'has-error' : ''}`}>
                <label htmlFor="confirmPassword" className="required">Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    className={errors.confirmPassword ? 'input-error' : ''}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon visible={showConfirmPassword} />
                  </button>
                </div>
                <small style={{ color: '#888', fontSize: '0.95rem' }}>
                  Must match the password entered above.
                </small>
                {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
              </div>
            </div>
          </div>

          {/* Submit Section */}
          <div className="form-section submit-section">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Registering...' : 'Submit Registration'}
            </button>
            
            <div className="login-link">
              <p>Already have an account? <Link to="/login">Login here</Link></p>
            </div>
          </div>
        </form>

        {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="terms-modal-header">
                <h2>Terms and Conditions</h2>
                <button 
                  className="terms-close-btn" 
                  onClick={() => setShowTermsModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              
              <div className="terms-modal-body">
                <h3>TG Jobs Telangana Portal - Terms of Use</h3>
                
                <h4>1. Acceptance of Terms</h4>
                <p>By registering on the TG Jobs Telangana Portal, you agree to comply with and be bound by these terms and conditions.</p>
                
                <h4>2. Registration Information</h4>
                <p>You agree to provide accurate, current, and complete information during the registration process. You are responsible for maintaining the confidentiality of your account credentials.</p>
                
                <h4>3. Privacy and Data Protection</h4>
                <p>Your personal information will be handled in accordance with applicable data protection laws. We will use your information only for employment-related purposes and will not share it with third parties without your consent, except as required by law.</p>
                
                <h4>4. Account Approval</h4>
                <p>Your registration is subject to approval by the portal administrator. The Government of Telangana reserves the right to approve, reject, or suspend any account at its discretion.</p>
                
                <h4>5. Prohibited Activities</h4>
                <p>You agree not to:</p>
                <ul>
                  <li>Provide false or misleading information</li>
                  <li>Use the portal for any unlawful purposes</li>
                  <li>Attempt to gain unauthorized access to the system</li>
                  <li>Upload malicious content or viruses</li>
                </ul>
                
                <h4>6. Government Services</h4>
                <p>This portal is a government initiative to facilitate employment opportunities for transgender persons in Telangana. Services are provided free of charge.</p>
                
                <h4>7. Contact Information</h4>
                <p>For any queries or concerns, please contact:</p>
                <p><strong>TG Helpline:</strong> 155326</p>
                <p><strong>Email:</strong> cdwtghyd@gmail.com</p>
                
                <h4>8. Modifications</h4>
                <p>The Government of Telangana reserves the right to modify these terms at any time. Continued use of the portal after modifications constitutes acceptance of the updated terms.</p>
              </div>
              
              <div className="terms-modal-footer">
                <button 
                  className="terms-decline-btn" 
                  onClick={() => setShowTermsModal(false)}
                >
                  Decline
                </button>
                <button 
                  className="terms-accept-btn" 
                  onClick={handleAcceptTerms}
                >
                  I Accept Terms & Conditions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidateRegistration;